import React from "react";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
} from "recharts";
import { fetchInfraErrorStats } from "../../services/infraApi";
import { isMobileViewport } from "../../utils/responsive";
import { getThemeColors } from "../../utils/themeColors";

class ErrorStatsPanel extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            stats: null,
            loading: true,
            error: null,
        };
        this._interval = null;
    }

    componentDidMount() {
        this.fetchData();
        this._interval = setInterval(this.fetchData, 10000);
    }

    componentWillUnmount() {
        if (this._interval) {
            clearInterval(this._interval);
        }
    }

    fetchData = () => {
        fetchInfraErrorStats()
            .then((res) => {
                this.setState({ stats: res?.data, loading: false, error: null });
            })
            .catch((err) => {
                this.setState({ error: err?.message || "통계 조회 실패", loading: false });
            });
    };

    render() {
        const { stats, loading, error } = this.state;
        const C = getThemeColors();

        if (loading) {
            return (
                <div className="rounded-2xl border border-surface-border bg-surface-card p-4 shadow-card sm:p-6">
                    <div className="text-sm text-content-secondary">에러 통계를 불러오는 중...</div>
                </div>
            );
        }

        if (error) {
            return (
                <div className="rounded-2xl border border-loss/30 bg-loss/10 p-4 shadow-card sm:p-6">
                    <div className="flex items-center gap-2">
                        <span className="inline-block h-2 w-2 rounded-full bg-loss" />
                        <span className="text-sm font-semibold text-loss">
                            에러 통계를 불러올 수 없습니다
                        </span>
                    </div>
                    <div className="mt-1 text-xs text-loss-light">{error}</div>
                </div>
            );
        }

        const totalErrors = stats?.total_errors ?? 0;
        const errorRate = stats?.recent_rate ?? 0;
        const uptime = stats?.uptime || "-";
        const byLevel = stats?.by_level || {};
        const byExchange = stats?.by_exchange || {};

        const exchangeChartData = Object.entries(byExchange).map(([name, count]) => ({
            name,
            count,
        }));

        return (
            <div className="rounded-2xl border border-surface-border bg-surface-card p-4 shadow-card sm:p-6">
                <h2 className="text-sm font-semibold text-content">에러 통계</h2>

                {/* 요약 행 */}
                <div className="mt-4 grid grid-cols-3 gap-3">
                    <div className="rounded-xl border border-surface-border bg-surface-elevated p-3">
                        <div className="text-xs font-semibold uppercase tracking-wide text-content-secondary">
                            총 에러
                        </div>
                        <div className="mt-1 text-xl font-bold text-content">{totalErrors.toLocaleString()}</div>
                    </div>
                    <div className="rounded-xl border border-surface-border bg-surface-elevated p-3">
                        <div className="text-xs font-semibold uppercase tracking-wide text-content-secondary">
                            분당 에러율
                        </div>
                        <div className="mt-1 text-xl font-bold text-content">
                            {typeof errorRate === "number" ? errorRate.toFixed(2) : "-"}
                        </div>
                    </div>
                    <div className="rounded-xl border border-surface-border bg-surface-elevated p-3">
                        <div className="text-xs font-semibold uppercase tracking-wide text-content-secondary">
                            가동 시간
                        </div>
                        <div className="mt-1 text-xl font-bold text-content">{uptime}</div>
                    </div>
                </div>

                {/* 레벨 배지 */}
                <div className="mt-4 flex flex-wrap gap-2">
                    {byLevel.ERROR !== undefined && (
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-loss-subtle px-3 py-1 text-sm font-semibold text-loss">
                            ERROR <span className="font-bold">{byLevel.ERROR.toLocaleString()}</span>
                        </span>
                    )}
                    {byLevel.WARN !== undefined && (
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-warning-subtle px-3 py-1 text-sm font-semibold text-warning">
                            WARN <span className="font-bold">{byLevel.WARN.toLocaleString()}</span>
                        </span>
                    )}
                    {byLevel.INFO !== undefined && (
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-subtle px-3 py-1 text-sm font-semibold text-brand-light">
                            INFO <span className="font-bold">{byLevel.INFO.toLocaleString()}</span>
                        </span>
                    )}
                </div>

                {/* 거래소별 바 차트 */}
                {exchangeChartData.length > 0 && (
                    <div className="mt-6">
                        <div className="mb-2 text-xs font-semibold text-content-secondary">거래소별 에러</div>
                        <ResponsiveContainer width="100%" height={Math.max(120, exchangeChartData.length * 36)}>
                            <BarChart
                                data={exchangeChartData}
                                layout="vertical"
                                margin={{ top: 0, right: 16, left: 8, bottom: 0 }}
                                isAnimationActive={false}
                            >
                                <XAxis type="number" tick={{ fontSize: 12, fill: C.contentSecondary }} />
                                <YAxis
                                    type="category"
                                    dataKey="name"
                                    tick={{ fontSize: 12, fill: C.contentSecondary }}
                                    width={isMobileViewport() ? 56 : 72}
                                />
                                <Tooltip
                                    formatter={(value) => [value.toLocaleString(), "에러 수"]}
                                    contentStyle={{
                                        backgroundColor: C.surfaceElevated,
                                        border: `1px solid ${C.surfaceBorder}`,
                                        borderRadius: "0.75rem",
                                    }}
                                />
                                <Bar dataKey="count" fill={C.contentSecondary} radius={[0, 4, 4, 0]} isAnimationActive={false} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                )}
            </div>
        );
    }
}

export default ErrorStatsPanel;
